import {Action} from '@reduxjs/toolkit'
import {persistReducer} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import {call, CallEffect, put, PutEffect, takeLatest} from 'redux-saga/effects'
import {AuthModel} from 'src/app/models'
import {TPermissions} from 'src/app/models/User/Permission.model'
import {fetchGlobalData, getProfile} from 'src/app/services'
import {getDetailPermission} from 'src/app/services/User/Permission.services'
import {ENV_STORAGE_KEY} from 'src/app/utils/env-utils'
import GlobalParamRedux from '../Global/GlobalParamRedux'

namespace AuthRedux {
  export interface ActionWithPayload<T> extends Action {
    payload?: T
  }

  export interface IAuthState {
    fetchingUser?: FetchingUserState
    user?: any
    accessToken?: string
    refreshtoken?: string
    permissions?: TPermissions[]
    sessionEnded?: boolean
  }

  export type FetchingUserState = 'failed' | 'success' | 'loading'

  export const actionTypes = {
    Login: '[Login] Action',
    Logout: '[Logout] Action',
    UserRequested: '[Request User] Action',
    RefreshToken: '[Refresh Token] Action',
    UserLoaded: '[Load User] Auth API',
    FetchingUser: '[Fetching User] Action',
    SetPermission: '[SetPermission] Action',
    SetEndSession: '[SetEndSession] Action',
  }

  export const initialAuthState: IAuthState = {
    user: undefined,
    accessToken: undefined,
    refreshtoken: undefined,
    permissions: [],
    sessionEnded: false,
  }

  export const reducer = () =>
    persistReducer<IAuthState, ActionWithPayload<IAuthState>>(
      {
        storage,
        key: 'FSM-auth',
        whitelist: ['accessToken', 'refreshtoken', 'user', 'permissions', 'sessionEnded'],
      },
      (state: IAuthState = initialAuthState, action = {type: ''}) => {
        switch (action.type) {
          case actionTypes.Login: {
            const accessToken = action.payload?.accessToken
            const refreshtoken = action.payload?.refreshtoken
            const user = action.payload?.user
            return {accessToken, refreshtoken, user}
          }

          case actionTypes.Logout: {
            return initialAuthState
          }

          case actionTypes.UserRequested: {
            return {...state}
          }

          case actionTypes.RefreshToken: {
            if (!state.refreshtoken) return state
            return {
              ...state,
              accessToken: action.payload?.accessToken ?? state.accessToken,
              refreshtoken: action.payload?.refreshtoken ?? state.refreshtoken,
            }
          }

          case actionTypes.UserLoaded: {
            const user = action.payload?.user
            return {...state, user, fetchingUser: 'success' as FetchingUserState}
          }

          case actionTypes.FetchingUser: {
            const fetchingUser = action.payload?.fetchingUser
            return {...state, fetchingUser}
          }

          case actionTypes.SetPermission: {
            const permissions = action.payload?.permissions
            return {...state, permissions}
          }

          case actionTypes.SetEndSession: {
            const sessionEnded = action.payload?.sessionEnded
            return {...state, sessionEnded}
          }

          default:
            return state
        }
      }
    )

  export const actions = {
    login: (accessToken: string, refreshtoken: string) => ({
      type: actionTypes.Login,
      payload: {accessToken, refreshtoken},
    }),
    logout: () => ({type: actionTypes.Logout}),
    requestUser: () => ({
      type: actionTypes.UserRequested,
    }),
    fulfillToken: ({token, refreshtoken}: AuthModel) => ({
      type: actionTypes.RefreshToken,
      payload: {accessToken: token, refreshtoken},
    }),
    fulfillUser: (user: any) => ({type: actionTypes.UserLoaded, payload: {user}}),
    setFetchingUser: (fetchingUser: 'failed' | 'success' | 'loading') => ({
      type: actionTypes.FetchingUser,
      payload: {fetchingUser},
    }),
    setPermission: (permissions: TPermissions[]) => ({
      type: actionTypes.SetPermission,
      payload: {permissions},
    }),
    setSessionEnded: (sessionEnded: boolean) => ({
      type: actionTypes.SetEndSession,
      payload: {sessionEnded},
    }),
  }

  export function* saga() {
    // yield takeLatest(actionTypes.RefreshToken, function* loginSaga() {
    //   yield put(actions.requestUser())

    //   yield call(fetchAndSetPermissions)
    //   yield call(fetchAndSetGlobalParams)
    // })
    yield takeLatest(actionTypes.Login, function* loginSaga() {
      yield put(actions.requestUser())

      yield call(fetchAndSetPermissions)
      yield call(fetchAndSetGlobalParams)
    })

    yield takeLatest(actionTypes.Logout, function* loginSaga() {
      const whitelist = {
        [ENV_STORAGE_KEY]: localStorage.getItem(ENV_STORAGE_KEY),
      }
      yield localStorage.clear()
      Object.entries(whitelist).forEach(([key, value]) => {
        if (value) localStorage.setItem(key, value)
      })
    })

    yield takeLatest(actionTypes.UserRequested, function* userRequested(_action: any) {
      try {
        yield put(actions.setFetchingUser('loading'))
        const {
          data: {
            response_output: {detail},
          },
        } = yield call(getProfile)

        yield put(actions.fulfillUser(detail))
      } catch {
        yield put(actions.setFetchingUser('failed'))
      }
    })
  }

  function* fetchAndSetGlobalParams(): Generator<CallEffect | PutEffect, void, any> {
    try {
      const data = yield call(fetchGlobalData)

      yield put(GlobalParamRedux.actions.setGlobalParams(data))
    } catch (error) {
      console.error('Error fetching global parameters:', error)
    }
  }

  function* fetchAndSetPermissions(): Generator<CallEffect | PutEffect, void, any> {
    try {
      const {
        data: {
          response_output: {detail},
        },
      } = yield call(getDetailPermission)

      yield put(actions.setPermission(detail))
    } catch (error) {
      console.error('Error fetching permission', error)
    }
  }
}

export default AuthRedux
